//july30 SettingsDashboard.jsx:
// src/pages/SettingsDashboard.jsx
import React, { useEffect, useState } from "react";
import { Box, Drawer, List, ListItem, ListItemText } from "@mui/material";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const drawerWidth = 240;

/* ------------------------------------------------------------------
 * ONE registry for every settings section.
 * 
 * Notes:
 * - `permissions`: AND semantics (user must have ALL listed).
 * - `anyOfPermissions`: OR semantics (user must have AT LEAST ONE).
 *   This is introduced to support the Asset Studio tab, where you
 *   want visibility if the user can *create OR edit OR publish OR delete*
 *   the org's Asset Upload Form — without granting global form powers.
 * ------------------------------------------------------------------ */
const SECTIONS = [
  {
    name: "My Profile",
    key:  "my-profile",
    permissions: [],                         // everyone
  },
  {
    name: "User Management",
    key:  "user-management",
    permissions: ["manageUsers"],            // admins / owners
  },
  {
    name: "Organization Settings",
    key:  "organization-settings",
    permissions: ["manageOrganization"],     // owners
  },
  {
    name: "SmartLists",
    key:  "smartlists",
    permissions: ["manageOrganization"],     // owners / org admins
  },
  {
    name: "Billing Settings",
    key:  "billing-settings",
    // allow owners OR billingManagers (they both have these perms)
    permissions: ["manageBilling", "viewInvoices"], // AND
  },

  /* ------------------------------------------------------------------
   * Asset Studio (DAM-lite)
   * - Appears **below Billing Settings** as requested.
   * - Uses OR semantics via `anyOfPermissions` so the tab shows up if
   *   the user has ANY of the DAM-form specific permissions, without
   *   relying on general form permissions.
   *
   * New DAM-form permissions you added:
   *   - createUploadAssetForm
   *   - editUploadAssetForm
   *   - publishUploadAssetForm
   *   - deleteUploadAssetForm  (optional)
   * ------------------------------------------------------------------ */
  {
    name: "Asset Studio",
    key:  "asset-studio",
    // Show if user has ANY of these permissions (OR semantics)
    anyOfPermissions: [
      "createUploadAssetForm",
      "editUploadAssetForm",
      "publishUploadAssetForm",
      "deleteUploadAssetForm" // optional but supported here
    ]
    // NOTE: Do NOT include `permissions` here, to avoid AND semantics.
  },
];

export default function SettingsDashboard() {
  /* --------------------------------------------- hooks & helpers */
  const navigate  = useNavigate();
  const location  = useLocation();
  const { hasPermission, userProfile, initialized } = useAuth();

  /* --------------------------------------------- state */
  const currentKey = location.pathname.split("/").pop();  // e.g. "my-profile"
  const [selectedPage, setSelectedPage] = useState(currentKey || "my-profile");

  /* ------------------------------------------------------------------
   * SmartLists owns its own left-side workspace panel.
   * Therefore, when we are on /settings/smartlists, we should NOT render
   * the default SettingsDashboard drawer, or we get a double-sidebar UI.
   * ------------------------------------------------------------------ */
  const isSmartListsRoute = currentKey === "smartlists";
  const shouldShowSettingsDrawer = !isSmartListsRoute;

  // Main content should expand to full width when SmartLists is active
  const mainContentWidth = shouldShowSettingsDrawer
    ? `calc(100% - ${drawerWidth}px)`
    : '100%';

  // Until AuthContext is ready, only show open-to-all items (permissions: [])
  const [menuItems, setMenuItems] = useState(
    SECTIONS.filter(s => Array.isArray(s.permissions) && s.permissions.length === 0)
  );

  /* 🔍  Measure the actual <AppBar> height so we can position the drawer */
  const [navHeight, setNavHeight] = useState(0);
  useEffect(() => {
    const measure = () => {
      const bar = document.querySelector(".my-app-bar");
      if (bar) setNavHeight(bar.offsetHeight || 0);
    };
    measure();                 // initial
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  /* ------------------------------------------------------------------
   *  Re-evaluate visible sections once AuthContext is ready
   *  - Supports both AND (`permissions`) and OR (`anyOfPermissions`)
   *    gating semantics.
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (!initialized) return; // avoid flicker before auth bootstraps

    const allowed = SECTIONS.filter((section) => {
      const { permissions, anyOfPermissions } = section;

      // Everyone
      if (Array.isArray(permissions) && permissions.length === 0) return true;

      // AND semantics: must have ALL listed permissions
      if (Array.isArray(permissions) && permissions.length > 0) {
        return hasPermission(permissions);
      }

      // OR semantics for Asset Studio (or future sections): at least one must pass
      if (Array.isArray(anyOfPermissions) && anyOfPermissions.length > 0) {
        return anyOfPermissions.some((perm) => hasPermission(perm));
      }

      // If no gating keys provided, default to hide (be explicit in registry)
      return false;
    });

    setMenuItems(allowed);
  }, [initialized, hasPermission]);

  /* --------------------------------------------- navigation */
  const handleNavigation = (item) => {
    setSelectedPage(item.key);
    navigate(`/settings/${item.key}`);
  };

  /* --------------------------------------------- render */
  return (
    <Box
      sx={{
        display : "flex",
        height  : `calc(100vh - ${navHeight}px)`, // fill visible viewport
      }}
    >
      {/* ───────── Sidebar ───────── */}
      {shouldShowSettingsDrawer && (
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              backgroundColor: "#f0f0f0",
              boxSizing: "border-box",
              position: "relative",
              height: "100%",
            },
          }}
        >
          <List>
            {menuItems.map((item) => (
              <ListItem
                button
                key={item.key}
                onClick={() => handleNavigation(item)}
                selected={selectedPage === item.key}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "transparent",
                    "& .MuiListItemText-root .MuiTypography-root": {
                      fontWeight: "bold",
                      color: "#1976d2",
                    },
                  },
                }}
              >
                <ListItemText primary={item.name} />
              </ListItem>
            ))}
          </List>
        </Drawer>
      )}

      {/* ───────── Main content ───────── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",

          // SmartLists renders its own workspace chrome, so remove the extra
          // dashboard padding there. Other settings pages keep the normal padding.
          p: isSmartListsRoute ? 0 : 3,

          overflowY: "auto",
          width: mainContentWidth,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}